import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { useTerminology } from "../../context/TerminologyContext";
import { api, toastError } from "../../lib/api";
import { hasWorkspaceAccess, type WorkspaceId } from "../../lib/workspaces";
import {
  Building2, Users, Contact2, KanbanSquare, LogOut, Shield,
  Settings2, FileClock, GraduationCap, Layers, Building, LayoutList,
  Upload, CalendarDays, BadgeDollarSign, LayoutDashboard, ListTodo, ClipboardCheck, FileBarChart, Receipt, BookOpen, FileText, MapPinned, PackageCheck, Megaphone, Lightbulb,
} from "lucide-react";

const linkCls = ({ isActive }) =>
  `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
    isActive
      ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
      : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
  }`;

type SidebarProps = {
  open?: boolean;
  onNavigate?: () => void;
};

export function Sidebar({ open = false, onNavigate }: SidebarProps) {
  const { user, logout, supportSession } = useAuth();
  const navigate = useNavigate();
  const effectiveRole = useEffectiveRole();
  const isOwnerAdmin = canAtLeast(effectiveRole, "owner_admin");
  const isManagerPlus = canAtLeast(effectiveRole, "manager_trainer");
  const isRepresentativePlus = canAtLeast(effectiveRole, "representative");
  const isAdminSupport = effectiveRole === "admin_support";
  const { term } = useTerminology();
  const [billingAttention, setBillingAttention] = useState(0);
  const isPlatform = Boolean(user && user.scope === "platform" && !supportSession);
  const show = (workspace: WorkspaceId) => hasWorkspaceAccess(user, workspace);
  useEffect(() => {
    if (!isPlatform) { setBillingAttention(0); return; }
    api.get("/platform/billing/summary")
      .then(({ data }) => setBillingAttention(data.attention_count || 0))
      .catch(() => undefined);
  }, [isPlatform]);
  if (!user) return null;

  return (
    <aside
      id="primary-navigation"
      aria-label="Primary navigation"
      className={`fixed inset-y-0 left-0 z-50 flex h-dvh w-72 flex-col max-w-[85vw] shrink-0 border-r border-slate-200 bg-white shadow-xl transition-transform duration-200 dark:border-slate-800 dark:bg-slate-950 dark:shadow-none md:sticky md:top-0 md:h-screen md:w-64 md:max-w-none md:translate-x-0 md:shadow-none ${open ? "translate-x-0" : "-translate-x-full"}`}
    >
      <div className="p-5 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-slate-900 dark:bg-slate-100 grid place-items-center">
            <Layers className="w-4 h-4 text-white dark:text-slate-900" />
          </div>
          <div>
            <div className="font-semibold text-sm tracking-tight">RemediKeep</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Phase 1</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-auto" data-testid="sidebar-nav" onClick={(event) => { if ((event.target as HTMLElement).closest("a")) onNavigate?.(); }}>
        {isPlatform && (
          <>
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-400">Platform</div>
            <NavLink to="/platform" end className={linkCls} data-testid="nav-platform-dashboard">
              <LayoutDashboard className="w-4 h-4" /> Control Center
            </NavLink>
            <NavLink to="/platform/tenants" className={linkCls} data-testid="nav-platform-tenants">
              <Building2 className="w-4 h-4" /> Tenants
            </NavLink>
            <NavLink to="/platform/support-access" className={linkCls} data-testid="nav-support-access">
              <Shield className="w-4 h-4" /> Tenant Support Access
            </NavLink>
            <NavLink to="/platform/billing" className={linkCls} data-testid="nav-platform-billing">
              <Receipt className="w-4 h-4" /> Invoices & Payments
              {billingAttention > 0 && <span className="ml-auto min-w-5 rounded-full bg-rose-600 px-1.5 py-0.5 text-center text-[10px] font-semibold text-white">{billingAttention > 99 ? "99+" : billingAttention}</span>}
            </NavLink>
            <NavLink to="/platform/announcements" className={linkCls} data-testid="nav-platform-announcements">
              <Megaphone className="w-4 h-4" /> System Announcements
            </NavLink>
          </>
        )}
        {!isPlatform && (
          <>
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-400">CRM</div>
            {show("dashboard") && (<NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </NavLink>)}
            {show("my_day") && (<NavLink to="/my-day" className={linkCls} data-testid="nav-my-day">
              <ListTodo className="w-4 h-4" /> My Day
            </NavLink>)}
            {show("tasks") && (<NavLink to="/tasks" className={linkCls} data-testid="nav-tasks">
              <ListTodo className="w-4 h-4" /> Task Tracker
            </NavLink>)}
            {show("contacts") && (<NavLink to="/contacts" className={linkCls} data-testid="nav-contacts">
              <Contact2 className="w-4 h-4" /> {term("contact", "plural")}
            </NavLink>)}
            {isRepresentativePlus && show("referrals") && (
              <NavLink to="/referrals" className={linkCls} data-testid="nav-referrals">
                <ClipboardCheck className="w-4 h-4" /> Referrals
              </NavLink>
            )}
            {show("organizations") && (<NavLink to="/organizations" className={linkCls} data-testid="nav-organizations">
              <Building className="w-4 h-4" /> Organizations
            </NavLink>)}
            {show("opportunities") && (<NavLink to="/opportunities" className={linkCls} data-testid="nav-opportunities">
              <KanbanSquare className="w-4 h-4" /> Opportunities
            </NavLink>)}
            {show("calendar") && (<NavLink to="/calendar" className={linkCls} data-testid="nav-calendar">
              <CalendarDays className="w-4 h-4" /> Calendar
            </NavLink>)}
            {(effectiveRole === "owner_admin" || effectiveRole === "admin_support") && (
              <NavLink to="/patient-calendar" className={linkCls} data-testid="nav-patient-calendar">
                <CalendarDays className="w-4 h-4" /> Patient Calendar
              </NavLink>
            )}
            {show("visit_verification") && (<NavLink to="/visit-verification" className={linkCls} data-testid="nav-visit-verification">
              <ClipboardCheck className="w-4 h-4" /> Visit Verification
            </NavLink>)}
            {canAtLeast(effectiveRole, "representative") && show("my_performance") && (
              <NavLink to="/my-performance" className={linkCls} data-testid="nav-my-performance">
                <BadgeDollarSign className="w-4 h-4" /> My Performance
              </NavLink>
            )}
            {canAtLeast(effectiveRole, "representative") && show("staff_invoices") && (
              <NavLink to="/staff-invoices" className={linkCls} data-testid="nav-staff-invoices">
                <FileText className="w-4 h-4" /> Staff Invoicing
              </NavLink>
            )}
            {canAtLeast(effectiveRole, "representative") && show("expenses") && (
              <NavLink to="/expenses" className={linkCls} data-testid="nav-expenses">
                <Receipt className="w-4 h-4" /> Reimbursement Requests
              </NavLink>
            )}
            {show("training") && (<NavLink to="/training" className={linkCls} data-testid="nav-training">
              <BookOpen className="w-4 h-4" /> Training
            </NavLink>)}
            <NavLink to="/equipment" className={linkCls} data-testid="nav-equipment">
              <PackageCheck className="w-4 h-4" /> {isManagerPlus ? "Equipment & Custody" : "My Equipment"}
            </NavLink>
            <NavLink to="/staff-requests" className={linkCls} data-testid="nav-staff-requests">
              <ClipboardCheck className="w-4 h-4" /> {isManagerPlus ? "Staff Requests" : "My Requests"}
            </NavLink>
            {isManagerPlus && <NavLink to="/announcements" className={linkCls} data-testid="nav-announcements">
              <Megaphone className="w-4 h-4" /> Announcements
            </NavLink>}
            {show("suggestions") && (
              <NavLink to="/suggestions" className={linkCls} data-testid="nav-suggestions">
                <Lightbulb className="w-4 h-4" /> Suggestion Box
              </NavLink>
            )}
            {canAtLeast(effectiveRole, "representative") && show("complaints") && (
              <NavLink to="/complaints" className={linkCls} data-testid="nav-complaints">
                <Shield className="w-4 h-4" /> {(effectiveRole === "owner_admin" || effectiveRole === "admin_support") ? "Complaints & Cases" : "Report a Concern"}
              </NavLink>
            )}
            {canAtLeast(effectiveRole, "representative") && show("coaching") && (
              <NavLink to="/coaching" className={linkCls} data-testid="nav-coaching">
                <GraduationCap className="w-4 h-4" /> {isManagerPlus ? "Coaching Notes" : "My Coaching"}
              </NavLink>
            )}
            {isManagerPlus && !isAdminSupport && show("user_360") && (
              <NavLink to="/user-360" className={linkCls} data-testid="nav-user-360">
                <Users className="w-4 h-4" /> User 360
              </NavLink>
            )}
            {isManagerPlus && show("imports") && (
              <NavLink to="/imports" className={linkCls} data-testid="nav-imports">
                <Upload className="w-4 h-4" /> Imports
              </NavLink>
            )}
            {((isManagerPlus && !isAdminSupport && show("reports")) ||
              (isRepresentativePlus && !isAdminSupport && show("referrals")) ||
              (isAdminSupport && show("referrals") && Boolean(user.referral_report_download))) && (
              <NavLink to="/reports" className={linkCls} data-testid="nav-reports">
                <FileBarChart className="w-4 h-4" /> Reports & Export Center
              </NavLink>
            )}
            {isOwnerAdmin && (
              <>
                <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-slate-400">Admin</div>
                <NavLink to="/admin/users" className={linkCls} data-testid="nav-users">
                  <Users className="w-4 h-4" /> Users
                </NavLink>
                <NavLink to="/admin/structure" className={linkCls} data-testid="nav-structure">
                  <Building2 className="w-4 h-4" /> Structure
                </NavLink>
                <NavLink to="/admin/memberships" className={linkCls} data-testid="nav-memberships">
                  <Users className="w-4 h-4" /> Memberships
                </NavLink>
                <NavLink to="/admin/commissions" className={linkCls} data-testid="nav-commissions">
                  <BadgeDollarSign className="w-4 h-4" /> Commissions
                </NavLink>
              </>
            )}
            {isManagerPlus && !isAdminSupport && <>
              <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-slate-400">Ops</div>
              <NavLink to="/config" className={linkCls} data-testid="nav-config">
                <Settings2 className="w-4 h-4" /> Configuration
              </NavLink>
              <NavLink to="/admin/territories" className={linkCls} data-testid="nav-territories">
                <MapPinned className="w-4 h-4" /> Territory Assignments
              </NavLink>
              <NavLink to="/data-quality" className={linkCls} data-testid="nav-data-quality">
                <Shield className="w-4 h-4" /> CRM Data Quality
              </NavLink>
            </>}
            {isManagerPlus && (
              <NavLink to="/audit" className={linkCls} data-testid="nav-audit">
                <FileClock className="w-4 h-4" /> Audit
              </NavLink>
            )}
          </>
        )}
      </nav>
      <div className="border-t border-slate-200 dark:border-slate-800 p-3">
        <div className="text-xs text-slate-500 mb-2 truncate" data-testid="current-user">{user.email}</div>
        <button
          onClick={async () => {
            onNavigate?.();
            try {
              await logout();
              navigate("/login", { replace: true });
            } catch (error) {
              toastError(error, "Sign out could not be confirmed. You are still signed in; please try again.");
            }
          }}
          className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-xs rounded-md border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>
    </aside>
  );
}

